import { forwardRef } from 'react';
import Barcode from 'react-barcode';

import moment from 'moment';

import { formatPersonName } from '@/utils';

const DATE_TIME_FORMAT = 'MM/DD/YYYY HH:mm';

interface PropertyLabelPrintProps {
  kase: {
    number?: string;
  };

  propertyDetails: {
    _id: string;

    number?: string;

    name?: string;

    typeTag?: string;

    involvement?: string;

    value?: number;

    quantity?: number;

    description?: string;

    loggedInfo?: {
      loggedBy?: {
        firstName?: string;

        lastName?: string;
      };

      loggedDate?: Date | string;
    };

    createdBy?: {
      firstName?: string;

      lastName?: string;
    };

    explosive?: {
      quantity?: number;
    };

    alcohol?: {
      quantity?: number;

      measurementType?: string;
    };

    drugs?: Array<{
      quantity?: number;

      measurementType?: string;
    }>;
  };

  cfsNumber?: string;

  propertyType?: string;
}

export const PropertyLabelPrint = forwardRef<
  HTMLDivElement,
  PropertyLabelPrintProps
>(({ kase, propertyDetails, cfsNumber, propertyType }, ref) => {
  // Handle logged by person

  const loggedBy = formatPersonName(propertyDetails?.loggedInfo?.loggedBy);

  // Calculate total quantity for drugs (sum of all drug items)

  const totalDrugQuantity = propertyDetails?.drugs?.reduce(
    (sum, drug) => sum + (drug.quantity || 0),

    0,
  );

  // Determine which quantity to display

  const displayQuantity =
    propertyDetails?.explosive?.quantity ||
    propertyDetails?.alcohol?.quantity ||
    totalDrugQuantity ||
    propertyDetails?.quantity ||
    null;

  return (
    <div ref={ref} className="thermal-label-container">
      <div className="thermal-label-content">
        {/* Header with Case ID and CFS ID */}

        <div className="header-section">
          <div className="case-number">{kase?.number || ''}</div>

          <div className="cfs-number">{cfsNumber || ''}</div>
        </div>

        {/* Barcode */}

        <div className="barcode-section">
          <Barcode
            value={propertyDetails.number || ''}
            height={50}
            width={1.8}
            fontSize={12}
            margin={0}
          />
        </div>

        {/* Property ID and Logged By */}

        <div className="info-section">
          <div className="property-id-block">
            <div className="label-text">PROPERTY ID</div>

            <div className="value-text">{propertyDetails?.number || ''}</div>
          </div>

          {loggedBy && (
            <div className="logged-by-block">
              <div className="label-text">LOGGED BY</div>

              <div className="logged-details">
                {propertyDetails?.loggedInfo?.loggedDate && (
                  <>
                    {moment(propertyDetails.loggedInfo.loggedDate).format(
                      DATE_TIME_FORMAT,
                    )}

                    <span className="dot"></span>
                  </>
                )}

                {loggedBy}
              </div>
            </div>
          )}
        </div>

        {/* Details Grid */}

        <div className="details-grid">
          {/* Name */}

          {propertyDetails?.name && (
            <div className="detail-item">
              <div className="detail-label">Name</div>

              <div className="detail-value">{propertyDetails.name}</div>
            </div>
          )}

          {/* Type */}

          {propertyType && (
            <div className="detail-item">
              <div className="detail-label">Type</div>

              <div className="detail-value">{propertyType}</div>
            </div>
          )}

          {/* Value */}

          {propertyDetails?.value != null && (
            <div className="detail-item">
              <div className="detail-label">Value</div>

              <div className="detail-value">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',

                  currency: 'USD',
                }).format(propertyDetails.value)}
              </div>
            </div>
          )}

          {/* Quantity */}

          {displayQuantity != null && (
            <div className="detail-item">
              <div className="detail-label">Quantity</div>

              <div className="detail-value">{displayQuantity}</div>
            </div>
          )}
        </div>
      </div>

      <style>{`

        * {

          margin: 0;

          padding: 0;

          box-sizing: border-box;

        }



        /* Container uses thermal width (4 inches x 3 inches) - FIXED HEIGHT */

        .thermal-label-container {

          width: 4in;

          height: 3in;

          max-height: 3in;

          margin: 0;

          padding: 0;

          background: white;

          position: relative;

          overflow: hidden;

          page-break-inside: avoid;

          break-inside: avoid;

        }



        /* Content has padding and uses available space */

        .thermal-label-content {

          width: 4in;

          height: 3in;

          max-height: 3in;

          padding: 0.15in 0.15in;

          box-sizing: border-box;

          background: white;

          color: black;

          font-family: Arial, sans-serif;

          display: flex;

          flex-direction: column;

          overflow: hidden;

        }



        .header-section {

          display: flex;

          justify-content: space-between;

          align-items: center;

          padding-bottom: 0.06in;

          margin-bottom: 0.08in;

          border-bottom: 1.5px solid black;

          flex-shrink: 0;

        }



        .case-number,

        .cfs-number {

          font-size: 16px;

          font-weight: 700;

          color: black;

          line-height: 1.1;

        }



        .barcode-section {

          display: flex;

          justify-content: center;

          align-items: center;

          margin-bottom: 0.08in;

          flex-shrink: 0;

          height: auto;

          max-height: 0.7in;

        }



        .barcode-section svg {

          max-width: 100%;

          height: auto;

          display: block;

        }



        .info-section {

          display: flex;

          justify-content: space-between;

          align-items: flex-start;

          padding-bottom: 0.06in;

          margin-bottom: 0.08in;

          border-bottom: 1.5px solid black;

          flex-shrink: 0;

        }



        .property-id-block,

        .logged-by-block {

          display: flex;

          flex-direction: column;

          max-width: 48%;

        }



        .logged-by-block {

          text-align: right;

          align-items: flex-end;

        }



        .label-text {

          font-size: 11px;

          font-weight: 700;

          color: black;

          margin-bottom: 0.03in;

          line-height: 1.1;

        }



        .value-text {

          font-size: 14px;

          font-weight: 400;

          color: black;

          line-height: 1.2;

        }



        .logged-details {

          font-size: 11px;

          font-weight: 400;

          color: black;

          display: flex;

          align-items: center;

          gap: 0.06in;

          line-height: 1.2;

          flex-wrap: wrap;

        }



        .dot {

          width: 3px;

          height: 3px;

          background: #000;

          border-radius: 50%;

          display: inline-block;

          flex-shrink: 0;

        }



        .details-grid {

          display: grid;

          grid-template-columns: 1fr 1fr;

          gap: 0.08in 0.1in;

          flex: 1 1 auto;

          align-content: start;

          overflow: hidden;

        }



        .detail-item {

          display: flex;

          flex-direction: column;

          min-height: 0;

          overflow: hidden;

        }



        .detail-label {

          font-size: 12px;

          font-weight: 700;

          color: black;

          margin-bottom: 0.03in;

          flex-shrink: 0;

          line-height: 1.1;

        }



        .detail-value {

          font-size: 12px;

          font-weight: 400;

          color: black;

          line-height: 1.3;

          word-wrap: break-word;

          overflow-wrap: break-word;

          hyphens: auto;

          white-space: normal;

          overflow: hidden;

          text-overflow: ellipsis;

        }



        @media print {

          @page {

            size: 4in 3in;

            margin: 0;

          }



          html,

          body {

            width: 4in;

            margin: 0 !important;

            padding: 0 !important;

          }



          body {

            print-color-adjust: exact;

            -webkit-print-color-adjust: exact;

          }



          .thermal-label-container {

            width: 4in !important;

            height: 3in !important;

            max-height: 3in !important;

            min-height: 3in !important;

            margin: 0 !important;

            padding: 0 !important;

            page-break-after: always;

            page-break-inside: avoid;

            break-after: page;

            break-inside: avoid;

            overflow: hidden !important;

          }



          .thermal-label-content {

            width: 4in !important;

            height: 3in !important;

            max-height: 3in !important;

            overflow: hidden !important;

          }

        }



        @media screen {

          .thermal-label-container {

            border: 1px dashed #ccc;

            margin: 20px auto;

          }

        }

      `}</style>
    </div>
  );
});

PropertyLabelPrint.displayName = 'PropertyLabelPrint';
